import * as React from "react"
import { Box, Flex, FlexList, NavButtonLink, NavLink } from "./ui"
import Caret from "./caret"
import * as styles from "./nav-item-group.css"
import { media } from "./ui.css"
import { desktopHeaderNavItem } from "./header.css"
import { NavItem, NavItemSubGroup } from "../data/navigation"
import { HeadingLine } from "../basic/heading-line"

interface NavItemGroupProps {
  name: string
  navItems: NavItemSubGroup[] | NavItem[]
  onLinkClick?: () => void
}

export default function NavItemGroup({
  name,
  navItems,
  onLinkClick,
}: NavItemGroupProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [popupVisible, setPopupVisible] = React.useState(false)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const popupRef = React.useRef<HTMLDivElement>(null)

  const isSmallScreen = React.useCallback(() => {
    return typeof window !== "undefined" && !window.matchMedia(media.small).matches
  }, [])

  const onTogglePopoverVisibility = React.useCallback(() => {
    if (!isOpen) {
      setIsOpen(true)
      setPopupVisible(true)
    } else {
      if (isSmallScreen()) {
        setIsOpen(false)
      }
      setPopupVisible(false)
    }
  }, [isOpen, isSmallScreen])

  React.useEffect(() => {
    const popupBox = popupRef.current
    if (!popupBox) return

    const onAnimationEnd = (event: AnimationEvent) => {
      if (event.animationName === "zoomOutDown") {
        setIsOpen(false)
      }
    }

    popupBox.addEventListener("animationend", onAnimationEnd)
    return () => {
      popupBox.removeEventListener("animationend", onAnimationEnd)
    }
  }, [isOpen])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const wrapper = wrapperRef.current
      if (
        !isSmallScreen() &&
        isOpen &&
        wrapper &&
        !wrapper.contains(event.target as Node)
      ) {
        onTogglePopoverVisibility()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen, isSmallScreen, onTogglePopoverVisibility])

  const handleLinkClick = () => {
    onLinkClick?.()
    onTogglePopoverVisibility()
  }

  const renderItem = (navItem: NavItem) => {
    return (
      <li key={navItem.id}>
        <NavLink
          to={navItem.href}
          className={styles.navLinkListLink}
          onClick={handleLinkClick}
        >
          <Flex variant="columnStart" marginY={1} gap={0}>
            <Box as="span" className={styles.navLinkTitle}>
              {navItem.text}
            </Box>
          </Flex>
        </NavLink>
      </li>
    )
  }

  const renderItemsForSubGroup = (subGroup: NavItemSubGroup) => {
    return subGroup.navItems.map((item) => renderItem(item))
  }

  const hasSubGroups = navItems.some(
    (navItem) => navItem.navItemType === "SubGroup"
  )

  return (
    <Flex
      ref={wrapperRef}
      variant="columnStart"
      gap={4}
      className={styles.navGroupWrapper}
    >
      <NavButtonLink
        style={{ textTransform: "uppercase" }}
        onClick={onTogglePopoverVisibility}
        className={desktopHeaderNavItem}
      >
        <Flex gap={1} className={styles.navGroupTitleInner}>
          {name}
          <Caret direction={isOpen ? "up" : "down"} />
        </Flex>
      </NavButtonLink>
      {isOpen && (
        <Box
          ref={popupRef}
          style={!hasSubGroups ? { minWidth: "100%" } : undefined}
          className={
            styles.navLinkListWrapper[popupVisible ? "opened" : "closed"]
          }
        >
          <FlexList
            variant="columnStart"
            gap={2}
            className={styles.navLinkListWrapperInner}
          >
            {navItems.map((navItem) => {
              if (navItem.navItemType === "SubGroup") {
                return (
                  <React.Fragment key={navItem.id}>
                    <Box>{navItem.name}</Box>
                    <Box>
                      <Flex wrap={true}>
                        {renderItemsForSubGroup(navItem)}
                      </Flex>
                    </Box>
                    <HeadingLine />
                  </React.Fragment>
                )
              } else {
                return (
                  <Flex key={navItem.id} wrap={true}>
                    {renderItem(navItem)}
                  </Flex>
                )
              }
            })}
          </FlexList>
        </Box>
      )}
    </Flex>
  )
}
